import React, { useState } from "react";
import { FaPlay } from "react-icons/fa";

/**
 * VideoCard – A Netflix-style video container.
 * Shows a poster thumbnail with play button; loads the real <video> only when the user clicks.
 *
 * Props:
 *  - src   : string (required) – video URL
 *  - poster: string (optional) – poster image URL
 *  - title : string (optional) – title displayed as overlay when idle
 */
const VideoCard = ({ src, poster, title }) => {
  const [isPlaying, setIsPlaying] = useState(false);

  return (
    <div className="relative overflow-hidden rounded-2xl bg-black/60 border border-white/10 shadow-lg group">
      {/* Idle state – show poster & play button */}
      {!isPlaying && (
        <>
          {poster ? (
            <img
              src={poster}
              alt={title || "Video poster"}
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              loading="lazy"
            />
          ) : (
            // Fallback: maintain 16:9 aspect ratio if no poster provided
            <div className="w-full h-0 pt-[56.25%] bg-black" />
          )}

          <button
            type="button"
            onClick={() => setIsPlaying(true)}
            className="absolute inset-0 flex items-center justify-center text-white/90 focus:outline-none"
            aria-label="Play video"
          >
            <FaPlay className="w-16 h-16 drop-shadow-lg opacity-90 transform group-hover:scale-110 transition-transform duration-200" />
          </button>

          {title && (
            <div className="absolute inset-x-0 bottom-0 p-4 bg-gradient-to-t from-black/80 to-transparent">
              <h3 className="text-lg font-semibold text-white truncate">
                {title}
              </h3>
            </div>
          )}
        </>
      )}

      {/* Playing state – show the actual <video> */}
      {isPlaying && (
        <video
          src={src}
          autoPlay
          controls
          playsInline
          className="w-full h-full object-contain bg-black"
        />
      )}
    </div>
  );
};

export default VideoCard;
